import * as React from "react";
import AutomationUtil from "sh-application/utility/AutomationUtil";
import setGlobalMessage from "../../actions/setGlobalMessage";
import StringsStore from "sh-strings/store";
import { appViewState } from "../../store";
import { GlobalMessageViewState } from "../../store/schema/GlobalMessageViewState";
import { Async, MessageBar } from "@fluentui/react";
import { MessageBar as FluentUIMessageBar, MessageBarActions, Button, MessageBarIntent, FluentProvider } from "@fluentui/react-components";
import { DismissRegular } from "@fluentui/react-icons";
import { observer } from "mobx-react";
import ThemeUtils from "sh-application/utility/ThemeUtils";
import { TEAM_PICKER_ICON_ID } from "../../../StaffHubConstants";

const styles = require("./GlobalMessageBar.scss");
const classNames = require("classnames/bind");

const AUTO_DISMISS_DELAY_MS = 2000;
const AUTO_DISMISS_ANIMATION_TIME_MS = 2000;

interface GlobalMessageBarProps {
    viewState?: GlobalMessageViewState;
    useNewMessageBar?: boolean;
}

interface GlobalMessageBarState {
    startAutoDismiss: boolean;
}

/**
 * GlobalMessageBar - MessageBar displayed in the StaffHub header area.
 */
@observer
export default class GlobalMessageBar extends React.Component<GlobalMessageBarProps, GlobalMessageBarState> {
    private _isDimissing = false;
    private _autoDismissDelayTimeout: number = 0;
    private _autoDismissAnimationTimeout: number = 0;
    private _currentMessageVersion: number = -1;
    private _async = new Async(this);

    constructor(props: GlobalMessageBarProps) {
        super(props);
        this.state = {
            startAutoDismiss: false
        };
    }

    /**
     * Maps MessageBarType to MessageBarIntent for new Fluent UI components
     */
    private mapMessageBarTypeToIntent(messageBarType: any): MessageBarIntent {
        switch (messageBarType) {
            case 0: // MessageBarType.info
                return "info";
            case 1: // MessageBarType.error
                return "error";
            case 4: // MessageBarType.success
                return "success";
            default:
                return "info";
        }
    }

    /**
     * Clear the global message and reset state.
     */
    private dismissMessageBar() {
        // Focus the hamburger menu
        const hamburgerMenu = document.getElementById(TEAM_PICKER_ICON_ID);
        if (hamburgerMenu) {
            hamburgerMenu.focus();
        }

        setGlobalMessage(appViewState().globalMessageViewState);
        this.resetAutoDimiss();
    }

    /**
     * Calculate and return the message bar props.
     */
    private getMessageBarProps() {
        let { viewState } = this.props;

        // setup the MessageBar props (MessageBar only shows if the viewState has a message)
        let messageBarProps = {
            messageBarType: viewState.messageBarType,
            onDismiss: viewState.autoDismiss ? null : () => this.dismissMessageBar(),   // don't show the close button if this is auto dimiss
            dismissButtonAriaLabel: viewState.autoDismiss ? null : StringsStore().registeredStringModules.get("common").strings.get("closeMessageAriaLabel"),
            actions: null as JSX.Element,
            isMultiline: viewState.isMultiline
        };

        // if there is an action, add it
        if (viewState.actionButtonTitle) {
            messageBarProps.actions =
                <div onClick={ () => viewState.actionButtonCallback() } className={ styles.messageBarLink }>
                    { viewState.actionButtonTitle }
                </div>;
        }

        return messageBarProps;
    }

    /**
     * Starts the auto dismiss process. We first delay for a set amount of time, then set the
     * state to autoDismiss (which kicks off the hide animation) and finally clean up by
     * calling dimissMessageBar once the animation is complete.
     */
    private autoDismiss() {
        if (!this._isDimissing) {
            this._isDimissing = true;
            this._autoDismissDelayTimeout = this._async.setTimeout(() => {
                this.setState({ startAutoDismiss: true });
                this._autoDismissAnimationTimeout = this._async.setTimeout(() => {
                    this.dismissMessageBar();
                }, this.props.viewState.autoDismissAnimationTimeout ?? AUTO_DISMISS_ANIMATION_TIME_MS);
            }, this.props.viewState.autoDismissDelay ?? AUTO_DISMISS_DELAY_MS);
        }
    }

    /**
     * Resets all auto dismiss state.
     */
    private resetAutoDimiss() {
        this._async.clearTimeout(this._autoDismissAnimationTimeout);
        this._async.clearTimeout(this._autoDismissDelayTimeout);
        this.setState({ startAutoDismiss: false });
        this._isDimissing = false;
    }

    /**
     * Cleans up timer when component is unmounted.
     */
    componentWillUnmount() {
        // clear all intervals
        this._async.dispose();
    }

    /**
     * Called when the GlobalMessageViewState is updated with property values. If the message has been updated we cancel any existing
     * auto dismiss delays and if autoDismiss is true, we start the auto dismiss process over.
     * @param nextProps
     */
    componentDidUpdate(prevProps: GlobalMessageBarProps) {
        const { viewState } = this.props;
        if (viewState && viewState.messages) {
            if (viewState.messageVersion !== this._currentMessageVersion) {
                this.resetAutoDimiss();
                this._currentMessageVersion = viewState.messageVersion ?? 0;
            }

            if (viewState.autoDismiss) {
                this.autoDismiss();
            }
        }
    }

    render() {
        let { viewState } = this.props;
        const { startAutoDismiss } = this.state;

        if (!viewState.messages) {
            return null;
        }

        if (viewState.useNewMessageBar) {
            return this.renderNewMessageBar();
        }

        return (
            <MessageBar
                data-automation-id={ AutomationUtil.getAutomationId("layout", "QAIDGlobalMessageBar") }
                aria-live={ "assertive" } // Assertive to make sure that it is read. Default is polite
                className={ classNames(styles.globalMessageBar, "ms-slideDownIn20", styles.messageBarTop, { center: viewState.isCenterAligned }, { autoDismiss: startAutoDismiss }) }
                { ...this.getMessageBarProps() } >
                { this.renderMessages() }
            </MessageBar>
        );
    }

    /**
     * Renders the new Fluent UI MessageBar component
     */
    private renderNewMessageBar() {
        let { viewState } = this.props;
        const { startAutoDismiss } = this.state;

        // Create action buttons array
        const actionButtons = [];

        // Add custom action button if provided
        if (viewState.actionButtonTitle) {
            actionButtons.push(
                <Button
                    key="action"
                    appearance="secondary"
                    shape="rounded"
                    onClick={() => viewState.actionButtonCallback()}
                    aria-label={viewState.actionButtonAriaLabel || viewState.actionButtonTitle}
                >
                    {viewState.actionButtonTitle}
                </Button>
            );
        }

        // Add dismiss button if not auto-dismiss and no action button provided
        if (!viewState.autoDismiss && !viewState.actionButtonTitle) {
            actionButtons.push(
                <Button
                    key="dismiss"
                    appearance="transparent"
                    icon={<DismissRegular />}
                    onClick={() => this.dismissMessageBar()}
                    aria-label={StringsStore().registeredStringModules.get("common").strings.get("closeMessageAriaLabel")}
                />
            );
        }

        const actions = actionButtons.length > 0 ? (
            <MessageBarActions
                containerAction={
                    <div style={{ display: 'flex', gap: '8px' }}>
                        {actionButtons}
                    </div>
                }
            />
        ) : undefined;

        const messageBarIntent = this.mapMessageBarTypeToIntent(viewState.messageBarType);

        return (
            <FluentProvider theme={ThemeUtils.getFluentV2ShiftTheme()}>
                <FluentUIMessageBar
                    data-automation-id={AutomationUtil.getAutomationId("layout", "QAIDGlobalMessageBar")}
                    className={classNames(styles.globalMessageBar, styles.messageBarTop, { center: viewState.isCenterAligned }, { autoDismiss: startAutoDismiss })}
                    intent={messageBarIntent}
                    layout={viewState.isMultiline ? "multiline" : "auto"}
                    politeness="assertive"
                    {...(viewState.icon ? { icon: viewState.icon } : {})}
                    shape="square"
                    style={{
                        backgroundColor: messageBarIntent === "info" ? "var(--colorBrandBackground2)" : undefined
                    }}
                >
                    <div className={styles.messageBarContentWrapper}>
                        <div className={styles.leftMessageWrapper}>{ this.renderMessages() }</div>
                        { viewState.secondaryMessage && <div className={styles.secondaryMessageWrapper}>{ viewState.secondaryMessage }</div> }
                    </div>
                    {actions}
                </FluentUIMessageBar>
            </FluentProvider>
        );
    }

    renderMessages(): JSX.Element[] {
        let { viewState } = this.props;
        let index = 0;
        if (viewState.messages) {
            let messages = viewState.messages.map((msg) => {
                return (
                    <span key={ index++ }>
                        { msg }
                        {
                            viewState.isMultiline &&
                                <br/>
                        }
                    </span>
                    );
                });
            return messages;
        } else {
            return null;
        }
    }
}